import React, { memo, useCallback, useMemo, useState } from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Button, Drawer } from "@mui/material";
import { AsyncContent } from "components";
import { FormikProps, useFormik } from "formik";
import { useDidUpdate } from "hooks";
import { capitalize, mapKeys, pickBy } from "lodash";
import { useReportsActions, useReportsDealFilterSelector, useReportsQueriesSelector } from "store";
import { ReportFilters } from "store/types";

import { AutocompleteMultiSelect } from "../AutocompleteMultiSelect/AutocompleteMultiSelect";

import "./FiltersDrawer.styles.scss";

const toCapitalizeList: Array<keyof ReportFilters> = ["round", "deal_name"];

interface ListProps {
  dealFilters: ReportFilters;
  values: ReportFilters;
  setFieldValue: FormikProps<ReportFilters>["setFieldValue"];
}

const List: React.FC<ListProps> = memo(({ dealFilters, values, setFieldValue }) => {
  return (
    <>
      {Object.keys(dealFilters).map((filter) => (
        <AutocompleteMultiSelect
          key={filter}
          multiple
          defaultValue={(values[filter] as Array<string | number>) || []}
          label={toCapitalizeList?.includes(filter) ? capitalize(filter).replace(/_/g, " ") : filter}
          options={(dealFilters[filter] || []).map(String)}
          limitTags={3}
          onChange={(value) => setFieldValue(filter, value)}
        />
      ))}
    </>
  );
});

interface FiltersDrawerProps {
  onClose: () => void;
  isOpen: boolean;
}

export const FiltersDrawer: React.FC<FiltersDrawerProps> = ({ onClose, isOpen }) => {
  const dealFilters = useReportsDealFilterSelector();
  const { filters } = useReportsQueriesSelector();
  const { setQueriesAction } = useReportsActions();
  const [loading, setLoading] = useState<boolean>(false);

  const recognizedFilters = useMemo(() => {
    const modifiedFilters = mapKeys(dealFilters, (value, key) => {
      if (key === "Deal Name") {
        return "deal_name";
      }
      return key;
    });
    const filteredResult = pickBy(modifiedFilters, (value) => value.length);
    return filteredResult;
  }, [dealFilters]);

  // Artificial delay before draw content
  const onOpen = useCallback(() => {
    if (!isOpen) return;
    setLoading(true);
    setTimeout(() => setLoading(false), 200);
  }, [isOpen]);

  useDidUpdate(onOpen, [isOpen]);

  const { values, handleSubmit, setFieldValue, resetForm } = useFormik({
    initialValues: filters,
    onSubmit: (values) => {
      const updatedValues = { ...values };
      Object.keys(updatedValues).forEach((filter) => {
        if (updatedValues[filter] === undefined || !updatedValues[filter].length) {
          delete updatedValues[filter];
        }
      });
      setQueriesAction({ filters: updatedValues });
      onClose();
    },
    enableReinitialize: true
  });

  const clearForm = useCallback(() => {
    // setEnableReinitialize(false);
    setLoading(true);
    const resetValues = { ...filters };
    for (const filter in resetValues) {
      resetValues[filter] = [];
    }
    resetForm();
    resetForm({ values: resetValues });
    setTimeout(() => setLoading(false), 100);
  }, []);

  return (
    <Drawer
      sx={{
        ".MuiPaper-root": {
          borderTopRightRadius: "25px",
          borderBottoRightRadius: "25px"
        }
      }}
      open={isOpen}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit} className="filters-drawer">
        <div className="head">
          <span className="title">Filter by Deal Characteristics</span>
          <CloseIcon className="icon" onClick={onClose} />
        </div>
        <AsyncContent spinnerProps={{ size: 100 }} loading={loading} className="body">
          <List dealFilters={recognizedFilters} values={values} setFieldValue={setFieldValue} />
        </AsyncContent>
        <div className="footer">
          <span onClick={clearForm} className="click-text">
            Clear All Filters
          </span>
          <div className="btn-block">
            <Button variant="outlined" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              Apply
            </Button>
          </div>
        </div>
      </form>
    </Drawer>
  );
};
